import { last } from "lodash";
import { zGuard } from "../zod/utils.js";
import hashObject, { NormalOption as ObjectHashOptions } from "object-hash";
import { z } from "zod";

import { CellType } from "../enums.js";
import {
  DataConnectionId,
  DataSourceTableId,
  SemanticDatasetName,
  SemanticViewName,
} from "../idTypeBrands.js";
import { HEX_HIDDEN_PREFIX } from "../languageUtils.js";

export const NoCodeCellTypeLiteral = z.union([
  z.literal(CellType.CHART),
  z.literal(CellType.PIVOT),
  z.literal(CellType.DISPLAY_TABLE),
  z.literal(CellType.FILTER),
  z.literal(CellType.EXPLORE),
]);
export type NoCodeCellType = z.infer<typeof NoCodeCellTypeLiteral>;

/**
 * Helper function for generating a valid dataframe name for a cell w/ a sql
 * config. Returns a unique dataframe name given a data connection Id, path to
 * table, and limit.
 */
export const generateDataframeNameForDataSourceTable = (
  dataSourceTableConfig: Pick<
    DataSourceTableConfig,
    "connectionId" | "pathToTable"
  >,
): string => {
  const { connectionId, pathToTable } = dataSourceTableConfig;
  // we generate a hash to ensure that we only have
  // alphanumeric characters in the dataframe name
  const hash = hashObject({ connectionId, pathToTable });
  const dataframeName = `${HEX_HIDDEN_PREFIX}${hash}`;
  return dataframeName;
};

export const generateDataframeNameForSemanticDatasetInput = (
  semanticDatasetInput: Pick<SemanticDatasetInput, "semanticDatasetName">,
  hashObjectOptions?: ObjectHashOptions,
): string => {
  const hash = hashObject(
    {
      semanticDatasetName: semanticDatasetInput.semanticDatasetName,
    },
    hashObjectOptions,
  );
  return `${HEX_HIDDEN_PREFIX}_${hash}`;
};

export const generateDataframeNameForSemanticViewInput = (
  semanticViewInput: Pick<SemanticViewInput, "semanticViewName">,
): string => {
  const hash = hashObject(
    { semanticViewName: semanticViewInput.semanticViewName },
    {
      // the prototype of a plain object does not matter
      // and would only potentially cause misalignments between platforms
      respectType: false,
    },
  );
  return `${HEX_HIDDEN_PREFIX}_${hash}`;
};

export const DataSourceTableConfig = z.object({
  dataframeName: z.string(),
  connectionId: DataConnectionId,
  pathToTable: z.string(),
  dataSourceTableId: DataSourceTableId,
});

export type DataSourceTableConfig = z.infer<typeof DataSourceTableConfig>;

export const SemanticDatasetInput = z.object({
  type: z.literal("SemanticDataset"),
  dataConnectionId: DataConnectionId,
  semanticDatasetName: SemanticDatasetName,
});

export type SemanticDatasetInput = z.infer<typeof SemanticDatasetInput>;

export const SemanticViewInput = z.object({
  type: z.literal("SemanticView"),
  dataConnectionId: DataConnectionId,
  semanticViewName: SemanticViewName,
});

export type SemanticViewInput = z.infer<typeof SemanticViewInput>;

/**
 * This can either be a dataframe name or an object that contains
 * metadata on the warehouse table + dataframe that contains the output of
 * the query to the warehouse table.
 */
export const NoCodeCellDataframe = z.union([DataSourceTableConfig, z.string()]);

export type NoCodeCellDataframe = z.infer<typeof NoCodeCellDataframe>;

export const ExploreCellDataframe = z.union([
  NoCodeCellDataframe,
  SemanticDatasetInput,
  SemanticViewInput,
]);

export type ExploreCellDataframe = z.infer<typeof ExploreCellDataframe>;

export function getDataConnectionIdFromDataframe(
  dataframe: unknown,
): DataConnectionId | undefined {
  if (zGuard(DataSourceTableConfig, dataframe)) {
    return dataframe.connectionId;
  }
  if (
    zGuard(SemanticDatasetInput, dataframe) ||
    zGuard(SemanticViewInput, dataframe)
  ) {
    return dataframe.dataConnectionId;
  }
  return undefined;
}

/**
 * This is only used for file schema generation.
 */
export const NullableNoCodeCellDataframe = z.union([
  NoCodeCellDataframe,
  z.null(),
]);
export const NullableExploreCellDataframe = z.union([
  ExploreCellDataframe,
  z.null(),
]);

/**
 * This takes a no code cell datafarme input and returns a string input
 * parameter. For normal dataframe inputs, this is just the name of the
 * dataframe, but for WHNC inputs, we want the input to be the path to the
 * table, to mirror what we do for SQL cells.
 */
export function getNoCodeCellInputReference(
  dataframe: ExploreCellDataframe,
): string {
  if (typeof dataframe === "string") {
    return dataframe;
  } else if (zGuard(SemanticDatasetInput, dataframe)) {
    return dataframe.semanticDatasetName;
  } else if (zGuard(SemanticViewInput, dataframe)) {
    return dataframe.semanticViewName;
  }
  // we strip the quotes to match the behavior of SQL cell references
  return stripQuotes(dataframe.pathToTable);
}

export function getDataframeName(dataframe: ExploreCellDataframe): string;
export function getDataframeName(
  dataframe: ExploreCellDataframe | null,
): string | null;
export function getDataframeName(
  dataframe: ExploreCellDataframe | null,
): string | null {
  if (typeof dataframe === "string" || dataframe == null) {
    return dataframe;
  } else if (zGuard(SemanticDatasetInput, dataframe)) {
    return generateDataframeNameForSemanticDatasetInput(dataframe);
  } else if (zGuard(SemanticViewInput, dataframe)) {
    return generateDataframeNameForSemanticViewInput(dataframe);
  }
  return dataframe.dataframeName;
}

/**
 * Returns just the table name, with any special quoting characters removed.
 */
export function getDataframeDisplayName(
  dataframe: ExploreCellDataframe,
): string;
export function getDataframeDisplayName(
  dataframe: ExploreCellDataframe | null,
): string | null;
export function getDataframeDisplayName(
  dataframe: ExploreCellDataframe | null,
): string | null {
  if (typeof dataframe === "string" || dataframe == null) {
    return dataframe;
  }

  if (zGuard(SemanticDatasetInput, dataframe)) {
    return dataframe.semanticDatasetName;
  }

  if (zGuard(SemanticViewInput, dataframe)) {
    return dataframe.semanticViewName;
  }

  return displayNameFromPathToTable(dataframe.pathToTable);
}

export function displayNameFromPathToTable(pathToTable: string): string {
  const lastPart = last(pathToTable.split(".")) ?? "";
  return stripQuotes(lastPart);
}

export function getPathToTableParts(dataframe: NoCodeCellDataframe): string[] {
  if (typeof dataframe === "string") {
    return [];
  }
  return dataframe.pathToTable.split(".").map(stripQuotes);
}

function stripQuotes(str: string): string {
  return str.replaceAll(new RegExp('["\\[\\]`]', "g"), "");
}
